import React from 'react';
import {
  Autocomplete,
  createFilterOptions,
  Box,
  Chip,
  Divider,
  Stack,
  TextField as MuiTextField,
  Typography,
} from '@mui/material';
import { Controller, useWatch } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { DASHBOARD_ROUTES } from 'Routes';
import RichTextEditorField from 'components/RichTextEditorField';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';
import { replaceFontFamily } from 'utils';

export const CustomDivider = ({ text }: { text: string }) => {
  return (
    <Divider
      textAlign="left"
      sx={{
        ' :before': {
          width: '0 !important',
        },
        ' :after': {
          width: '100% !important',
        },
        '&::before, &::after': {
          borderColor: '#213B54',
        },
        color: '#213B54',
        fontWeight: 700,
        fontSize: '14px',
        '& .MuiDivider-wrapper': {
          paddingLeft: '0px',
        },
        marginTop: '12px',
      }}
    >
      {text}
    </Divider>
  );
};

export interface RecipientFieldValue {
  userId: string;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const recipientLabel = (option: RecipientFieldValue): string =>
  [option.firstName, option.lastName].filter(Boolean).join(' ') ||
  option.email ||
  'Unknown user';

// Out-of-roster recipients (pills for users no longer in the roster, collab
// #76) must still render, but only until they're removed — they simply don't
// come back in the searchable list. Folding whatever is already selected
// into the option set gets both for free: MUI's Autocomplete only offers
// options for re-selection, so a removed, no-longer-listed value just drops.
const mergeOptionsWithValue = (
  roster: RecipientFieldValue[],
  current: RecipientFieldValue[],
): RecipientFieldValue[] => {
  const rosterIds = new Set(roster.map((option) => option.userId));
  const outOfRoster = current.filter((option) => !rosterIds.has(option.userId));
  return [...roster, ...outOfRoster];
};

// A saved contact the roster doesn't offer: someone at a company this record
// isn't linked to. The backend refuses to save one, so the page names them
// before Save does. Only meaningful once the roster has actually loaded.
export const outOfRoster = (
  recipients: RecipientFieldValue[],
  roster: RecipientFieldValue[],
): RecipientFieldValue[] => {
  const rosterIds = new Set(roster.map((option) => option.userId));
  const seen = new Set<string>();
  return recipients.filter((recipient) => {
    if (rosterIds.has(recipient.userId) || seen.has(recipient.userId)) {
      return false;
    }
    seen.add(recipient.userId);
    return true;
  });
};

export const joinNames = (recipients: RecipientFieldValue[]): string => {
  const names = recipients.map(recipientLabel);
  return names.length > 1
    ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
    : names[0] || '';
};

// The backend rejects a user picked as both To and Cc on the same category
// (400 — "at most once per category"), and the only feedback the admin sees
// is a generic "Update failed!" toast. Removing the sibling role's picks
// from each picker's offered options stops the conflict before it's created.
const excludeUserIds = (
  options: RecipientFieldValue[],
  excluded: string[],
): RecipientFieldValue[] => {
  if (!excluded.length) return options;
  const excludedIds = new Set(excluded);
  return options.filter((option) => !excludedIds.has(option.userId));
};

// Matches on name AND email so a search by email actually finds the person —
// the chip/option label only ever shows the name, so the default label-only
// filter silently found nothing for an email query despite the placeholder
// promising "Search by name or email".
const filterRecipientOptions = createFilterOptions<RecipientFieldValue>({
  // MUI's default filter strips diacritics ("Jose" finds "José"); keep that
  // and add the email to the searched text.
  stringify: (option) => `${recipientLabel(option)} ${option.email || ''}`,
});

// The name and email sit in separate spans so the name stays findable on its
// own, and a contact with no email (or whose label already IS the email)
// renders without an empty "()".
const recipientChipLabel = (option: RecipientFieldValue): React.ReactNode => {
  const label = recipientLabel(option);
  if (!option.email || option.email === label) return label;
  return (
    <>
      <span>{label}</span>
      <Box component="span" sx={{ fontWeight: 500, color: '#7D818C' }}>
        {` (${option.email})`}
      </Box>
    </>
  );
};

const STALE_TITLE =
  'Not an active admin of this company or its parent company. Remove to save.';

const recipientChipTitle = (
  option: RecipientFieldValue,
  stale = false,
): string => {
  const label = recipientLabel(option);
  const title =
    option.email && option.email !== label
      ? `${label} (${option.email})`
      : label;
  return stale ? `${title} — ${STALE_TITLE}` : title;
};

const STALE_COLOR = '#8A5300';

const chipSx = {
  backgroundColor: '#E4EBF2',
  color: '#213B54',
  fontWeight: 700,
  fontSize: '13px',
  height: '28px',
  maxWidth: '100%',
  '& .MuiChip-deleteIcon': {
    color: '#7D818C',
  },
  '& .MuiChip-deleteIcon:hover': {
    color: '#213B54',
  },
};

const staleChipSx = {
  ...chipSx,
  backgroundColor: '#FDF1E3',
  color: STALE_COLOR,
  border: '1px dashed #D9A35B',
};

const StaleRecipientsNote = ({ stale }: { stale: RecipientFieldValue[] }) =>
  stale.length ? (
    <Typography role="status" sx={{ fontSize: '13px', color: STALE_COLOR }}>
      {joinNames(stale)}{' '}
      {stale.length > 1 ? 'are not active admins' : 'is not an active admin'} of
      this company or its parent company, so they cannot receive triages. Remove
      them to save.
    </Typography>
  ) : null;

const sectionDescriptionSx = {
  fontSize: '15px',
  fontWeight: 500,
  lineHeight: '24px',
};

const readLabelSx = {
  fontSize: '13px',
  fontWeight: 700,
  color: '#7D818C',
  minWidth: '48px',
};

interface RecipientAutocompleteProps {
  label: string;
  name: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  roster: RecipientFieldValue[];
  // The sibling field's current picks (Cc's userIds when this is To, and
  // vice versa) — excluded from this picker's options so the same person
  // can't be added to both roles in one category.
  excludeUserIds?: string[];
  staleIds: Set<string>;
}

const RecipientAutocomplete = ({
  label,
  name,
  control,
  roster,
  excludeUserIds: exclude = [],
  staleIds,
}: RecipientAutocompleteProps) => {
  return (
    <Controller
      control={control}
      name={name}
      defaultValue={[]}
      render={({ field: { value, onChange, ref } }) => {
        const current: RecipientFieldValue[] = value || [];
        const availableRoster = excludeUserIds(roster, exclude);
        return (
          <Autocomplete
            multiple
            id={name}
            options={mergeOptionsWithValue(availableRoster, current)}
            value={current}
            filterSelectedOptions
            filterOptions={filterRecipientOptions}
            getOptionLabel={recipientLabel}
            isOptionEqualToValue={(option, val) => option.userId === val.userId}
            onChange={(_, newValue) => onChange(newValue)}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => (
                <Chip
                  {...getTagProps({ index })}
                  key={option.userId}
                  label={recipientChipLabel(option)}
                  title={recipientChipTitle(
                    option,
                    staleIds.has(option.userId),
                  )}
                  sx={staleIds.has(option.userId) ? staleChipSx : chipSx}
                />
              ))
            }
            renderInput={(params) => (
              <MuiTextField
                {...params}
                inputRef={ref}
                label={label}
                variant="outlined"
                placeholder={
                  current.length ? undefined : 'Search by name or email'
                }
              />
            )}
          />
        );
      }}
    />
  );
};

interface RecipientReadListProps {
  label: string;
  recipients: RecipientFieldValue[];
  staleIds: Set<string>;
}

const linkChipSx = {
  ...chipSx,
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: '#D3DDE8',
  },
  '&.Mui-focusVisible': {
    outline: '2px solid #213B54',
    outlineOffset: '2px',
  },
};

const staleLinkChipSx = {
  ...linkChipSx,
  ...staleChipSx,
  '&:hover, &.Mui-focusVisible': {
    backgroundColor: '#FAE3C6',
  },
};

const RecipientReadList = ({
  label,
  recipients,
  staleIds,
}: RecipientReadListProps) => (
  <Stack direction={'row'} alignItems={'flex-start'} gap={1.5}>
    <Typography sx={readLabelSx}>{label}:</Typography>
    {recipients.length ? (
      <Stack direction={'row'} flexWrap={'wrap'} gap={1} sx={{ minWidth: 0 }}>
        {recipients.map((recipient) => (
          <Chip
            key={recipient.userId}
            component={Link}
            to={DASHBOARD_ROUTES.USER_DETAIL_ID(recipient.userId)}
            clickable
            label={recipientChipLabel(recipient)}
            title={recipientChipTitle(
              recipient,
              staleIds.has(recipient.userId),
            )}
            sx={staleIds.has(recipient.userId) ? staleLinkChipSx : linkChipSx}
          />
        ))}
      </Stack>
    ) : (
      <Typography sx={{ fontSize: '13px', color: '#7D818C' }}>
        None assigned
      </Typography>
    )}
  </Stack>
);

export interface EscalationTriageSectionProps {
  title: string;
  description: string;
  edit: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  toName: string;
  ccName: string;
  notesName: string;
  roster: RecipientFieldValue[];
  // False while the roster is loading or after it failed to load: an empty
  // roster then means "unknown", not "nobody", so nothing is marked.
  rosterLoaded: boolean;
}

export const EscalationTriageSection = ({
  title,
  description,
  edit,
  control,
  toName,
  ccName,
  notesName,
  roster,
  rosterLoaded,
}: EscalationTriageSectionProps) => {
  const toValue: RecipientFieldValue[] =
    useWatch({ control, name: toName }) || [];
  const ccValue: RecipientFieldValue[] =
    useWatch({ control, name: ccName }) || [];
  const notesValue: string = useWatch({ control, name: notesName }) || '';
  const stale = rosterLoaded
    ? outOfRoster([...toValue, ...ccValue], roster)
    : [];
  const staleIds = new Set(stale.map((recipient) => recipient.userId));

  return (
    <Stack gap={2}>
      <CustomDivider text={title} />
      <Typography sx={sectionDescriptionSx}>{description}</Typography>
      {edit ? (
        <Stack gap={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
            <Box flex={1}>
              <RecipientAutocomplete
                label="To"
                name={toName}
                control={control}
                roster={roster}
                excludeUserIds={ccValue.map((recipient) => recipient.userId)}
                staleIds={staleIds}
              />
            </Box>
            <Box flex={1}>
              <RecipientAutocomplete
                label="Cc"
                name={ccName}
                control={control}
                roster={roster}
                excludeUserIds={toValue.map((recipient) => recipient.userId)}
                staleIds={staleIds}
              />
            </Box>
          </Stack>
          <StaleRecipientsNote stale={stale} />
          <RichTextEditorField
            name={notesName}
            label="Notes"
            control={control}
            placeholder="Add a note"
            editorStyle={{
              border: '1px solid #79747E',
              borderRadius: '4px',
              paddingRight: '12px',
              paddingLeft: '12px',
            }}
            wrapperClassName="custom-editor"
          />
        </Stack>
      ) : (
        <Stack gap={1.5}>
          <RecipientReadList
            label="To"
            recipients={toValue}
            staleIds={staleIds}
          />
          <RecipientReadList
            label="Cc"
            recipients={ccValue}
            staleIds={staleIds}
          />
          <StaleRecipientsNote stale={stale} />
          <Stack direction={'row'} alignItems={'flex-start'} gap={1.5}>
            <Typography sx={readLabelSx}>Notes:</Typography>
            <Typography sx={{ fontSize: '13px' }}>
              {notesValue
                ? parse(DOMPurify.sanitize(replaceFontFamily(notesValue)))
                : ''}
            </Typography>
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};

export default EscalationTriageSection;
